import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["container", "hiddenField", "submitButton"];
  static values = { emailRegexp: String };

  connect() {
    if (this._isRequestAccessPage()) {
      this._setupRequestAccessModal();
      return;
    }
    // State
    this.emails = [];
    this.input = this._getInput();
    this._bindInputKeydown = this.handleInputKeydown.bind(this);
    this._bindInputChange = this.handleInputChange.bind(this);
    this.input.addEventListener("keydown", this._bindInputKeydown);
    this.input.addEventListener("input", this._bindInputChange);
    this.renderChips();

    // Event listeners
    this._setupShareButton();
    this._setupModalCloseHandler();
    this._setupSuccessModalHandler();
    this._setupCheckboxAccessibility();
    this._addFerpaLinkSpaceActivation();
  }

  disconnect() {
    if (this._requestAccessMode) {
      this._deactivateFocusTrap(this._requestAccessModalId);
      return;
    }
    // Clean up all event listeners
    const shareBtn = document.getElementById('shareButton');
    if (shareBtn && this._shareBtnHandler) shareBtn.removeEventListener('click', this._shareBtnHandler);
    const shareModal = document.getElementById('shareModal');
    if (shareModal && this._modalClickHandler) shareModal.removeEventListener('click', this._modalClickHandler);
    if (this._showSuccessModalHandler) document.removeEventListener('share-report-success', this._showSuccessModalHandler);
    if (this.input && this._bindInputKeydown) this.input.removeEventListener("keydown", this._bindInputKeydown);
    if (this.input && this._bindInputChange) this.input.removeEventListener("input", this._bindInputChange);
    this._cleanupCheckboxAccessibility();
    this._removeFerpaLinkSpaceActivation();
  }

  _isRequestAccessPage() {
    return document.getElementById('requestAccessModal') && !document.getElementById('shareModal');
  }

  _setupRequestAccessModal() {
    this._requestAccessMode = true;
    this._requestAccessModalId = 'requestAccessModal';
    this._requestAccessBackdropId = 'request-access-backdrop';
    this.showModal(this._requestAccessModalId, this._requestAccessBackdropId);
  }

  // --- Private event setup helpers ---
  _setupShareButton() {
    // Share button opens modal
    this._shareBtnHandler = () => this.showShareModal();
    const shareBtn = document.getElementById('shareButton');
    if (shareBtn) shareBtn.addEventListener('click', this._shareBtnHandler);
  }

  _setupModalCloseHandler() {
    // Event delegation for close/cancel in modal
    this._modalClickHandler = (e) => {
      if (e.target.closest('.btn-close, .cancl')) {
        this.hideModal('shareModal', 'main-backdrop');
      }
    };
    const shareModal = document.getElementById('shareModal');
    if (shareModal) shareModal.addEventListener('click', this._modalClickHandler);
  }

  _setupSuccessModalHandler() {
    // Turbo Stream custom event for success modal
    this._showSuccessModalHandler = () => this.showSuccessModal();
    document.addEventListener('share-report-success', this._showSuccessModalHandler);
  }

  handleInputKeydown(e) {
    if (["Enter", " ", "Tab"].includes(e.key)) {
      e.preventDefault();
      const email = this._pendingEmail();
      if (!email) return;
      if (!this.isValidEmail(email)) {
        this.showEmailError("Please enter a valid email address.");
        return;
      }
      if (this.emails.includes(email)) {
        this.showEmailError("This email has already been added.");
        return;
      }
      this.addEmail(email);
      this.clearEmailError();
    } else if (e.key === "Backspace" && !this.input.value) {
      this.emails.pop();
      this.renderChips();
    }
  }

  handleInputChange() {
    this.clearEmailError();
    this.toggleSubmitButton();
  }

  _getInput() {
    // Prefer Stimulus target if available, fallback to DOM
    if (this.hasContainerTarget) {
      const input = this.containerTarget.querySelector('#email-input');
      if (input) return input;
    }
    return document.getElementById('email-input');
  }

  _setupCheckboxAccessibility() {
    // Keyboard accessibility for checkboxes (main and confirmation modals)
    this._checkboxKeyHandler = (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        e.target.checked = !e.target.checked;
        e.target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };
    this._sendCopyCheckboxKeyHandler = this._checkboxKeyHandler;
    this._confirmSendCheckboxKeyHandler = this._checkboxKeyHandler;
    setTimeout(() => {
      [
        { id: 'sendCopyCheckbox', handler: this._sendCopyCheckboxKeyHandler },
        { id: 'confirmSendCheckbox', handler: this._confirmSendCheckboxKeyHandler }
      ].forEach(({ id, handler }) => {
        const el = document.getElementById(id);
        if (el) el.addEventListener('keydown', handler);
      });
    }, 0);
  }

  _cleanupCheckboxAccessibility() {
    [
      { id: 'sendCopyCheckbox', handler: this._sendCopyCheckboxKeyHandler },
      { id: 'confirmSendCheckbox', handler: this._confirmSendCheckboxKeyHandler }
    ].forEach(({ id, handler }) => {
      const el = document.getElementById(id);
      if (el && handler) el.removeEventListener('keydown', handler);
    });
  }

  showEmailError(message) {
    // Show inline error and mark input invalid
    const errorDiv = document.getElementById('email-error');
    if (errorDiv) {
      errorDiv.textContent = message;
      errorDiv.classList.remove('visually-hidden');
    }
    if (this.input) {
      this.input.setAttribute('aria-invalid', 'true');
    }
  }

  clearEmailError() {
    // Hide error and remove invalid state
    const errorDiv = document.getElementById('email-error');
    if (errorDiv) {
      errorDiv.textContent = '';
      errorDiv.classList.add('visually-hidden');
    }
    if (this.input) {
      this.input.removeAttribute('aria-invalid');
    }
  }

  /* ---------------------------------------------------
   * Modal helpers
   * --------------------------------------------------- */
  showModal(id, backdropId) {
    // Show modal and trap focus
    const modal = document.getElementById(id);
    if (!modal) return;
    modal.classList.remove("d-none");
    modal.classList.add("show");
    modal.style.display = "block";
    document.body.classList.add("modal-open");
    if (!document.getElementById(backdropId)) {
      const backdrop = document.createElement("div");
      backdrop.id = backdropId;
      backdrop.className = "modal-backdrop fade show";
      document.body.appendChild(backdrop);
    }
    // Focus the first focusable element (email input or close button)
    setTimeout(() => {
      const focusable = modal.querySelectorAll('input, button, [tabindex]:not([tabindex="-1"])');
      if (focusable.length) focusable[0].focus();
    }, 0);
    this._activateFocusTrap(id);
  }

  _addFerpaLinkSpaceActivation() {
    // Allow Space to activate FERPA link for accessibility
    const ferpaLink = document.querySelector('.ferpa-link');
    if (!ferpaLink) return;
    this._ferpaLinkKeyHandler = (e) => {
      if (e.key === ' ') {
        e.preventDefault();
        ferpaLink.click();
      }
    };
    ferpaLink.addEventListener('keydown', this._ferpaLinkKeyHandler);
  }

  _removeFerpaLinkSpaceActivation() {
    const ferpaLink = document.querySelector('.ferpa-link');
    if (ferpaLink && this._ferpaLinkKeyHandler) {
      ferpaLink.removeEventListener('keydown', this._ferpaLinkKeyHandler);
    }
  }

  hideModal(id, backdropId) {
    const modal = document.getElementById(id);
    if (!modal) return;
    modal.classList.add("d-none");
    modal.classList.remove("show");
    modal.style.display = "";
    this.cleanupBackdrops();
    this._deactivateFocusTrap(id);
  }

  cleanupBackdrops() {
    document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
    document.body.classList.remove('modal-open');
  }

  /* ---------------------------------------------------
   * Success modal (Turbo Stream based)
   * --------------------------------------------------- */
  showSuccessModal() {
    // Show success modal and reset form state
    this.showModal("shareReportSuccessModal", "success-backdrop");
    // Reset share modal fields after successful submission
    const form = document.getElementById("share-report-form");
    if (form) {
      form.reset();
      form.dataset.confirmed = "";
    }
    this.emails = [];
    this.renderChips();
  }

  closeSuccessModal() {
    this.hideModal("shareReportSuccessModal", "success-backdrop");
  }

  showShareModal() {
    this.showModal("shareModal", "main-backdrop");
    this.interceptForm();
  }

  /* ---------------------------------------------------
   * Confirmation flow
   * --------------------------------------------------- */
  interceptForm() {
    // Intercept form submit for confirmation modal
    const form = document.getElementById("share-report-form");
    if (!form) return;
    if (this._formSubmitHandler) form.removeEventListener("submit", this._formSubmitHandler);
    this._formSubmitHandler = (e) => {
      this.commitPendingEmail();
      if (!form.dataset.confirmed) {
        e.preventDefault();
        this.hideModal("shareModal", "main-backdrop");
        this.showModal("shareReportConfirmationModal", "confirm-backdrop");
      }
    };
    form.addEventListener("submit", this._formSubmitHandler);
  }

  closeConfirmationModal() {
    this.hideModal("shareReportConfirmationModal", "confirm-backdrop");
    this.showModal("shareModal", "main-backdrop");
  }

  confirmShare() {
    const checkbox = document.getElementById("confirmSendCheckbox");
    const form = document.getElementById("share-report-form");
    if (!checkbox?.checked) return checkbox?.focus();
    this.hideModal("shareReportConfirmationModal", "confirm-backdrop");
    this.hideModal("shareModal", "main-backdrop");
    form.dataset.confirmed = "true";
    form.requestSubmit();
  }

  toggleShareButton() {
    const checkbox = document.getElementById("confirmSendCheckbox");
    const button = document.getElementById("confirmShareReportBtn");
    if (checkbox && button) button.disabled = !checkbox.checked;
  }

  /* ---------------------------------------------------
   * Email chips input
   * --------------------------------------------------- */

  addEmail(email) {
    if (!email || !this.isValidEmail(email) || this.emails.includes(email)) return;
    this.emails.push(email);
    this.input.value = "";
    this.renderChips();
  }

  commitPendingEmail() {
    this.addEmail(this._pendingEmail());
  }

  _pendingEmail() {
    return this.input?.value?.trim() || "";
  }

  _activateFocusTrap(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) return;
    this._focusTrapHandler = (e) => {
      if (!modal.classList.contains('show')) return;
      // Trap focus with Tab/Shift+Tab
      if (e.key === 'Tab') {
        const focusable = Array.from(modal.querySelectorAll('a[href], button:not([disabled]):not([tabindex="-1"]), textarea, input:not([type=hidden]), select, [tabindex]:not([tabindex="-1"])'))
          .filter(el => el.offsetParent !== null && !el.disabled && modal.contains(el));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!modal.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
          return;
        }
        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      }
      // Close modal on Escape
      if (e.key === 'Escape') {
        e.preventDefault();
        this.hideModal(modalId, 'main-backdrop');
      }
    };
    document.addEventListener('keydown', this._focusTrapHandler, true);
  }

  _deactivateFocusTrap(modalId) {
    if (this._focusTrapHandler) {
      document.removeEventListener('keydown', this._focusTrapHandler, true);
      this._focusTrapHandler = null;
    }
  }

  renderChips() {
    // Render email chips and keep input accessible
    const fragment = document.createDocumentFragment();
    this.emails.forEach((email, index) => {
      const pill = document.createElement("div");
      pill.className =
        "filter-easy-clear-button d-flex align-items-center bg-light rounded-pill border px-3 py-1 me-1 mb-1";

      const span = document.createElement("span");
      span.textContent = email;

      const button = document.createElement("button");
      button.type = "button";
      button.className = "ms-2 text-dark fs-5 text-decoration-none p-0 bg-transparent border-0 shadow-none";
      button.setAttribute("aria-label", `Remove email ${email}`);
      button.style.lineHeight = "1";
      button.onclick = e => {
        e.preventDefault();
        this.emails.splice(index, 1);
        this.renderChips();
      };
      button.innerHTML = "&times;";

      pill.appendChild(span);
      pill.appendChild(button);
      fragment.appendChild(pill);
    });
    this.containerTarget.innerHTML = "";
    this.containerTarget.appendChild(fragment);
    if (!this.containerTarget.contains(this.input)) {
      this.containerTarget.appendChild(this.input);
    }
    this.hiddenFieldTarget.value = this.emails.join(",");
    this.input.focus();
    this.toggleSubmitButton();
  }

  toggleSubmitButton() {
    if (!this.hasSubmitButtonTarget) return;
    this.submitButtonTarget.disabled = this.emails.length === 0 && !this.isValidEmail(this._pendingEmail());
  }

  isValidEmail(email) {
    return !email.includes(",") && new RegExp(this.emailRegexpValue).test(email);
  }
}
